// src/features/tidsregistering/pages/SalaryExportPage.jsx
import { useTranslation } from 'react-i18next';
import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Alert, Button,
} from '@mui/material';
import {
  FileDownload as DownloadIcon,
  Refresh as LoadIcon,
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useStaff } from '../../staff/hooks/useStaff';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { balancesAPI } from '../services/balances';
import StaffMember from '../../../components/common/StaffMember';

import PageHeader from '../../../components/common/PageHeader';
import PaymentsIcon from '@mui/icons-material/Payments';

function num(v) {
  return Number(v ?? 0).toFixed(2);
}

function exportToCSV(rows, staff, periodStart, periodEnd) {
  const header = [
    'Medarbejder',
    'Normaltid (t)',
    'Merarbejde frivillig (t)',
    'Merarbejde beordret (t)',
    'Overarbejde (t)',
    'Forskudttid (t)',
    'Ferie (dage)',
    'Feriefridage (t)',
    'Afspadsering (t)',
    'Uddannelse (t)',
    'Fritvalg (t)',
    'Lønreduktion (t)',
    'Sygefravær total (dage)',
    'Sygefravær arbejdsgiver (dage)',
    'Sygefravær kommune (dage)',
    'Barsel fødende (uger)',
    'Barsel ikke-fødende (uger)',
    'Delt barsel (uger)',
    'Pension arbejdsgiver (kr)',
    'Pension medarbejder (kr)',
  ];

  const csvRows = rows.map((r) => {
    const member = staff.find((s) => s.staff_member_id === r.staff_member_id);
    return [
      member?.name ?? r.staff_member_id,
      num(r.normaltid_hours),
      num(r.merarbejde_voluntary_hours),
      num(r.merarbejde_forced_hours),
      num(r.overarbejde_hours),
      num(r.forskudttid_hours),
      num(r.ferie_days_used),
      num(r.feriefri_hours_used),
      num(r.afspadsering_hours_used),
      num(r.uddannelse_hours_used),
      num(r.fritvalg_deductions),
      num(r.salary_reduction_hours),
      num(r.sick_leave_days_total),
      num(r.sick_leave_days_employer),
      num(r.sick_leave_days_municipality),
      num(r.barsel_foedende_weeks),
      num(r.barsel_ikke_foedende_weeks),
      num(r.barsel_delt_weeks),
      num(r.pension_employer_amount),
      num(r.pension_employee_amount),
    ].join(';');
  });

  const csv = [header.join(';'), ...csvRows].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `loeneksport_${periodStart}_${periodEnd}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function SalaryExportPage() {
  const { t } = useTranslation(['tidsregistering', 'common']);
  const { showError } = useSnackbar();
  const { staff, fetchDepartmentStaff, loading: staffLoading } = useStaff();

  const lastMonth = subMonths(new Date(), 1);
  const [periodStart, setPeriodStart] = useState(startOfMonth(lastMonth));
  const [periodEnd, setPeriodEnd] = useState(endOfMonth(lastMonth));

  const [exports, setExports] = useState([]);
  const [loadingExports, setLoadingExports] = useState(false);
  const [exportError, setExportError] = useState(null);

  const handleLoad = useCallback(async () => {
    const staffList = staff.length > 0 ? staff : await fetchDepartmentStaff().then(() => staff);
    if (!staff.length) { await fetchDepartmentStaff(); return; }

    setLoadingExports(true);
    setExportError(null);

    const startStr = format(periodStart, 'yyyy-MM-dd');
    const endStr = format(periodEnd, 'yyyy-MM-dd');

    const results = await Promise.allSettled(
      staff.map((s) => balancesAPI.getSalaryExport(s.staff_member_id, startStr, endStr))
    );

    const successful = results
      .filter((r) => r.status === 'fulfilled')
      .map((r) => r.value.data);

    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      setExportError(`${failed} medarbejder(e) kunne ikke hentes`);
    }

    setExports(successful);
    setLoadingExports(false);
  }, [staff, fetchDepartmentStaff, periodStart, periodEnd]);

  // Load staff on mount
  React.useEffect(() => { fetchDepartmentStaff(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const staffMap = Object.fromEntries((staff ?? []).map((s) => [s.staff_member_id, s]));

  const columns = [
    { key: 'normaltid_hours', label: 'Normaltid (t)' },
    { key: 'merarbejde_voluntary_hours', label: 'Merarbejde friv. (t)' },
    { key: 'merarbejde_forced_hours', label: 'Merarbejde beo. (t)' },
    { key: 'overarbejde_hours', label: 'Overarbejde (t)' },
    { key: 'ferie_days_used', label: 'Ferie (dage)' },
    { key: 'feriefri_hours_used', label: 'Feriefridage (t)' },
    { key: 'afspadsering_hours_used', label: 'Afspadsering (t)' },
    { key: 'sick_leave_days_total', label: 'Sygefravær (dage)' },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        mb={4}
        icon={PaymentsIcon}
        title={t('common:navigation.tidsregistering_salary_export')}
        subtitle={t('tidsregistering:page_subtitles.salary_export')}
      />

      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <>
          <DatePicker
            label="Fra"
            value={periodStart}
            onChange={(d) => d && setPeriodStart(d)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label="Til"
            value={periodEnd}
            onChange={(d) => d && setPeriodEnd(d)}
            slotProps={{ textField: { size: 'small' } }}
          />
        </>

        <Button
          variant="contained"
          startIcon={loadingExports ? <CircularProgress size={14} color="inherit" /> : <LoadIcon />}
          onClick={handleLoad}
          disabled={loadingExports || staffLoading}
        >
          Hent data
        </Button>

        {exports.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => exportToCSV(exports, staff, format(periodStart, 'yyyy-MM-dd'), format(periodEnd, 'yyyy-MM-dd'))}
          >
            Download CSV
          </Button>
        )}
      </Box>

      {exportError && <Alert severity="warning">{exportError}</Alert>}

      <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 180 }}>Medarbejder</TableCell>
              {columns.map((c) => (
                <TableCell key={c.key} align="right" sx={{ whiteSpace: 'nowrap' }}>{c.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingExports ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : exports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                  Klik "Hent data" for at indlæse løndata for perioden
                </TableCell>
              </TableRow>
            ) : exports.map((row) => {
              const member = staffMap[row.staff_member_id];
              return (
                <TableRow key={row.staff_member_id} hover>
                  <TableCell>
                    {member
                      ? <StaffMember staffMember={member} avatarSize={24} gap={1} nameVariant="body2" clickable={false} />
                      : <Typography variant="body2" color="text.disabled">{row.staff_member_id.slice(0, 8)}…</Typography>
                    }
                  </TableCell>
                  {columns.map((c) => (
                    <TableCell key={c.key} align="right">
                      <Typography variant="body2">{num(row[c.key])}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
